import { useMemo } from "react"
import { type Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDate from "~/ui-rtk/hooks/date"

const dayjs = useDate()

const CUBE = "company_web_analytics_page_path_report"

// Maturity window (in days) for each LTV breakeven timeframe. The cohort
// window is ended `windowDays + 1` days ago so every cohort in range is old
// enough to have a fully-matured LTV figure (e.g. a 365-day LTV needs cohorts
// at least 366 days old). `first_order` carries no LTV horizon, so it reuses
// the 90d window and its behavior is unchanged.
const WINDOW_DAYS: Record<string, number> = {
  first_order: 90,
  "90d": 90,
  "180d": 180,
  "365d": 365,
}

// Incremental-LTV measure added on top of first-order revenue for each
// timeframe. `first_order` adds nothing (first-order revenue only).
const LTV_MEASURE: Record<string, string | null> = {
  first_order: null,
  "90d": `${CUBE}.sum_first_order_ltv_90_day`,
  "180d": `${CUBE}.sum_first_order_ltv_180_day`,
  "365d": `${CUBE}.sum_first_order_ltv_365_day`,
}

// Width (in days) of the cohort acquisition band, preserved across timeframes.
// For the 90d case this yields today-270d .. today-91d, matching prior behavior.
const COHORT_BAND_DAYS = 179

interface UseRecommendedRoasParams {
  grossMargin: number
  shippingCost: number
  ccFees: number
  spendIntoMarginalLtv: string
}

interface UseRecommendedRoasResult {
  recommendedDRRoas: number | null
  recommendedBrandRoas: number | null
  isLoading: boolean
  hasData: boolean
}

export function useRecommendedRoas({
  grossMargin,
  shippingCost,
  ccFees,
  spendIntoMarginalLtv,
}: UseRecommendedRoasParams): UseRecommendedRoasResult {
  const query: CubeQuery = useMemo(() => {
    const windowDays = WINDOW_DAYS[spendIntoMarginalLtv] ?? 90
    const ltvMeasure = LTV_MEASURE[spendIntoMarginalLtv] ?? null

    // End the window so only fully-matured cohorts are included, then look
    // back over the original 179-day acquisition band.
    const endDate = dayjs()
      .subtract(windowDays + 1, "day")
      .format("YYYY-MM-DD")
    const startDate = dayjs()
      .subtract(windowDays + 1 + COHORT_BAND_DAYS, "day")
      .format("YYYY-MM-DD")

    const measures = [`${CUBE}.sum_first_order_revs`]
    if (ltvMeasure) {
      measures.push(ltvMeasure)
    }

    return {
      measures,
      timeDimensions: [
        {
          dimension: `${CUBE}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
      limit: 1,
    }
  }, [spendIntoMarginalLtv])

  const { resultSet, isLoading } = useCubeQuery(query)

  const result = useMemo(() => {
    if (!resultSet) {
      return {
        recommendedDRRoas: null,
        recommendedBrandRoas: null,
        isLoading,
        hasData: false,
      }
    }

    const rows = resultSet.tablePivot()
    if (!rows.length) {
      return {
        recommendedDRRoas: null,
        recommendedBrandRoas: null,
        isLoading: false,
        hasData: false,
      }
    }

    const row = rows[0]
    const firstOrderRevenue = Number(row[`${CUBE}.sum_first_order_revs`]) || 0
    const ltvMeasure = LTV_MEASURE[spendIntoMarginalLtv] ?? null
    const incrementalLtv = ltvMeasure ? Number(row[ltvMeasure]) || 0 : 0

    if (firstOrderRevenue <= 0) {
      return {
        recommendedDRRoas: null,
        recommendedBrandRoas: null,
        isLoading: false,
        hasData: false,
      }
    }

    const totalRevenueOverLTV = firstOrderRevenue + incrementalLtv

    const breakevenSpend =
      totalRevenueOverLTV * (grossMargin - shippingCost - ccFees)

    if (breakevenSpend <= 0) {
      return {
        recommendedDRRoas: null,
        recommendedBrandRoas: null,
        isLoading: false,
        hasData: true,
      }
    }

    const targetDRRoas = firstOrderRevenue / breakevenSpend
    const targetBrandRoas = targetDRRoas * 1.5

    return {
      recommendedDRRoas: Math.round(targetDRRoas * 100) / 100,
      recommendedBrandRoas: Math.round(targetBrandRoas * 100) / 100,
      isLoading: false,
      hasData: true,
    }
  }, [
    resultSet,
    isLoading,
    grossMargin,
    shippingCost,
    ccFees,
    spendIntoMarginalLtv,
  ])

  return result
}
